import ContentHandler from '../schema/ContentHandler';
import { lazyMountFluidComponent, lazyTryGetFluidDriverForUrl } from 'owa-fluid-integrations';
import {
    FluidLoadSource,
    FluidLoadOwaScenario,
    fluidComponentLoaded,
    fluidComponentError,
} from 'owa-fluid-datapoints';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyGetODBSharedItem } from 'owa-fileprovider-link-services-lazy';

export const FLUID_HANDLER_NAME = 'fluidHandler';
const FLUID_SELECTOR = 'a';
const ORIGINALSRC_ATTTIBUTE_NAME = 'originalsrc';

export class FluidHandler implements ContentHandler {
    public readonly cssSelector = FLUID_SELECTOR;
    public readonly keywords = null;
    private storeElements: HTMLElement[] = [];
    private linksHydrated: string[] = [];

    constructor() {
        this.storeElements = [];
    }

    public readonly handler = (element: HTMLElement) => {
        const href = element.hasAttribute(ORIGINALSRC_ATTTIBUTE_NAME)
            ? element.getAttribute(ORIGINALSRC_ATTTIBUTE_NAME)
            : (element as HTMLAnchorElement).href;
        //Links without text in them (i.e. image links) should not be hydrated
        //If we've already hydrated a specific link before, don't hydrate it again.
        const trimmedContent = element.textContent.trim();
        if (trimmedContent && this.linksHydrated.indexOf(href) == -1) {
            // add it here, if it fails remove it.
            this.linksHydrated.push(href);
            lazyTryGetFluidDriverForUrl.import().then(tryGetFluidDriverForUrl => {
                const driverInfo = tryGetFluidDriverForUrl(href);
                if (driverInfo) {
                    lazyMountFluidComponent.import().then(mountFluidComponent => {
                        if (mountFluidComponent && element && document.body.contains(element)) {
                            const placeholderDiv = element.ownerDocument.createElement('div');
                            element.insertAdjacentElement('afterend', placeholderDiv);
                            mountFluidComponent(
                                placeholderDiv,
                                driverInfo,
                                element,
                                undefined /* idOfPlaceholder */,
                                undefined /*containerConfig */,
                                href
                            )
                                .then(() => {
                                    this.cleanupAfterSuccess(placeholderDiv);
                                })
                                .catch(error => {
                                    if (isFeatureEnabled('rp-redeemBohemiaLinksOnFailure')) {
                                        lazyGetODBSharedItem
                                            .importAndExecute(href)
                                            .then(() => {
                                                // nuke the current contents of the div
                                                placeholderDiv.textContent = null;
                                                mountFluidComponent(
                                                    placeholderDiv,
                                                    driverInfo,
                                                    element
                                                )
                                                    .then(() => {
                                                        this.cleanupAfterSuccess(
                                                            placeholderDiv,
                                                            true /* wasRetry */
                                                        );
                                                    })
                                                    .catch(error => {
                                                        this.cleanupAfterFailure(
                                                            placeholderDiv,
                                                            href,
                                                            error
                                                        );
                                                    });
                                            })
                                            .catch(error => {
                                                this.cleanupAfterFailure(
                                                    placeholderDiv,
                                                    href,
                                                    error
                                                );
                                            });
                                    } else {
                                        this.cleanupAfterFailure(placeholderDiv, href, error);
                                    }
                                });
                        }
                    });
                }
            });
        }
    };

    private cleanupAfterFailure = (placeholderDiv: HTMLElement, href: string, error: Error) => {
        placeholderDiv.parentNode.removeChild(placeholderDiv);
        this.linksHydrated.splice(this.linksHydrated.indexOf(href), 1);
        fluidComponentError(FluidLoadOwaScenario.ReadingPane, FluidLoadSource.Bohemia, error);
    };

    private cleanupAfterSuccess = (placeholderDiv: HTMLElement, wasRetry?: boolean) => {
        this.storeElements.push(placeholderDiv);
        fluidComponentLoaded(FluidLoadOwaScenario.ReadingPane, FluidLoadSource.Bohemia, wasRetry);
    };

    public readonly undoHandler = (elements: HTMLElement[]) => {
        this.storeElements.forEach(placeholderDiv => {
            placeholderDiv.parentNode.removeChild(placeholderDiv);
        });
        this.storeElements = [];
        this.linksHydrated = [];
    };
}

export default function createFluidHandler(): FluidHandler {
    return new FluidHandler();
}
